/**
 * Entity context — fetches entities once and provides a lookup by ID.
 * Avoids every page independently resolving entity names from mock data.
 */

import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react';
import { fetchEntities, getEntities, type Entity } from '@/lib/data';

const isMockData = import.meta.env.VITE_MOCK_DATA === 'true';

interface EntityContextValue {
  entities: Entity[];
  loading: boolean;
  getEntityName: (id: string | undefined) => string | undefined;
}

const EntityContext = createContext<EntityContextValue>({
  entities: [],
  loading: true,
  getEntityName: () => undefined,
});

export function EntityProvider({ children }: { children: ReactNode }) {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const result = isMockData ? getEntities() : await fetchEntities();
        setEntities(result);
      } catch {
        // Fallback to mock if fetch fails
        setEntities(getEntities());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const lookup = useMemo(() => {
    const map = new Map(entities.map((e) => [e.id, e.name]));
    return (id: string | undefined) => (id ? map.get(id) : undefined);
  }, [entities]);

  return (
    <EntityContext.Provider value={{ entities, loading, getEntityName: lookup }}>
      {children}
    </EntityContext.Provider>
  );
}

export function useEntityName(id: string | undefined): string | undefined {
  const { getEntityName } = useContext(EntityContext);
  return getEntityName(id);
}

export function useEntityContext() {
  return useContext(EntityContext);
}
